import React, { useEffect, useState } from "react";
import { Mic, Check, X, Hand, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { IconButton } from "@/components/ui/icon-button";
import { haptics } from "@/lib/interactions/haptics";

export interface VoiceOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  isRecording?: boolean;
  isProcessing?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
}

export const VoiceOverlay: React.FC<VoiceOverlayProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isRecording = false,
  isProcessing = false,
  isError = false,
  onRetry,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showHandHint, setShowHandHint] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Show hand hint only on first open (idle state)
      if (!isRecording && !isProcessing && !isError) {
        setShowHandHint(true);
        const timer = setTimeout(() => setShowHandHint(false), 3000);
        return () => clearTimeout(timer);
      }
    } else {
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen, isRecording, isProcessing, isError]);

  // Haptic feedback for recording state changes
  useEffect(() => {
    if (isRecording) {
      // Strong impact when recording starts
      haptics.impact();
    }
  }, [isRecording]);

  // Haptic feedback for error state
  useEffect(() => {
    if (isError) {
      // Error vibration pattern
      haptics.error();
    }
  }, [isError]);

  if (!isVisible && !isOpen) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex items-center justify-center",
        "bg-background/95 backdrop-blur-md",
        "transition-opacity duration-300",
        isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        className
      )}
      onClick={onClose}
    >
      <div
        className="flex flex-col items-center gap-8 p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Microphone Button Container */}
        <div className="relative flex items-center justify-center">
          {/* Hand tap hint - shows on idle state */}
          {showHandHint && !isRecording && !isProcessing && !isError && (
            <div className="absolute -top-12 animate-hand-tap">
              <Hand className="w-8 h-8 text-primary" />
            </div>
          )}

          {/* Ripple rings during recording */}
          {isRecording && (
            <>
              <div className="absolute inset-0 w-20 h-20 rounded-full border-2 border-primary/40 animate-ripple-ring" />
              <div 
                className="absolute inset-0 w-20 h-20 rounded-full border-2 border-primary/30 animate-ripple-ring"
                style={{ animationDelay: "0.5s" }}
              />
            </>
          )}

          {/* Processing spinner */}
          {isProcessing && (
            <div className="absolute inset-0 w-20 h-20">
              <svg className="animate-spin w-full h-full" viewBox="0 0 50 50">
                <circle
                  className="opacity-25"
                  cx="25"
                  cy="25"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  style={{ color: "hsl(var(--primary))" }}
                />
                <circle
                  className="opacity-75"
                  cx="25"
                  cy="25"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray="80"
                  strokeDashoffset="60"
                  style={{ color: "hsl(var(--primary))" }}
                />
              </svg>
            </div>
          )}

          {/* Main microphone button */}
          <button
            className={cn(
              "relative w-20 h-20 rounded-full flex items-center justify-center",
              "transition-all duration-300 shadow-voice z-10",
              isError && "animate-shake",
              isProcessing && "opacity-50",
              isRecording && "bg-primary/90 scale-105",
              !isRecording && !isProcessing && !isError && "bg-primary animate-glow-pulse",
              !isRecording && !isProcessing && isError && "bg-destructive"
            )}
            aria-label={
              isProcessing ? "Processing..." :
              isRecording ? "Recording..." : 
              isError ? "Error occurred" :
              "Start recording"
            }
          >
            <Mic className={cn("w-8 h-8 text-white", isProcessing && "opacity-50")} />
          </button>

          {/* Sound wave bars during recording */}
          {isRecording && (
            <div className="absolute flex gap-1 items-end justify-center pointer-events-none">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-primary rounded-full animate-wave-bar"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                    animationDuration: `${0.6 + Math.random() * 0.4}s`,
                    marginLeft: i === 2 ? "96px" : i < 2 ? `${(2 - i) * -16}px` : `${(i - 2) * 16}px`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Recording Indicator */}
        {isRecording && (
          <div className="flex items-center gap-2 animate-pulse">
            <div className="w-3 h-3 rounded-full bg-voice-active" />
            <span className="text-sm font-medium text-foreground">
              Recording...
            </span>
          </div>
        )}

        {/* Processing Indicator */}
        {isProcessing && (
          <div className="text-sm font-medium text-muted-foreground">
            Processing...
          </div>
        )}

        {/* Error State with Retry */}
        {isError && onRetry && (
          <button
            onClick={() => {
              haptics.warning();
              onRetry();
            }}
            className="flex flex-col items-center gap-2 group"
          >
            <RefreshCw className="w-6 h-6 text-destructive animate-pulse group-hover:rotate-180 transition-transform duration-500" />
            <span className="text-sm font-medium text-destructive">
              Tap to retry
            </span>
          </button>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              haptics.tap();
              onClose();
            }}
            className="w-14 h-14 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center shadow-md active:scale-95 transition-all"
            aria-label="Cancel"
          >
            <X className="w-6 h-6" />
          </button>
          {onConfirm && (
            <button
              onClick={() => {
                haptics.success();
                onConfirm();
              }}
              className="w-14 h-14 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center shadow-md active:scale-95 transition-all"
              aria-label="Confirm"
            >
              <Check className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
